import { BasicReference, ensureReference, } from "@knyt/artisan";
import { EventStation } from "event-station";
import { HostMonitor } from "../HostMonitor.js";
import { listenTo } from "../listenTo.js";
import { ContextEventName, ContextProviderAvailableEvent, ContextRequestEvent, } from "./events.js";
export class BaseProvider extends BasicReference {
    #host;
    #station = new EventStation();
    #emitter$;
    #hostMonitor;
    constructor(host, { initialValue, target, }) {
        super({
            initialValue,
            onUpdate: (value) => {
                if (this.#hostMonitor.isHostConnected) {
                    this.#station.emit(ContextEventName.ValueUpdate, value);
                }
            },
        });
        this.#host = host;
        this.#emitter$ = ensureReference(target);
        this.#hostMonitor = new HostMonitor(host);
        listenTo(this.#host, this.#emitter$)
            .add(ContextEventName.ContextRequest, (contextRequest) => {
            if (!this._isRelevantContextRequest(contextRequest)) {
                // Ignore requests for other contexts.
                return;
            }
            this.#onContextRequest(contextRequest);
        })
            .add(ContextEventName.ContextProviderAvailable, () => {
            this.#onContextProviderAvailable();
        });
        host.addController(this);
    }
    #isConsumerSubscribed(handleValueUpdate) {
        return !!this.#station.getListeners(ContextEventName.ValueUpdate, handleValueUpdate);
    }
    #onContextRequest(contextRequest) {
        // Stop the event from propagating to other providers.
        // At this point, we're assuming that the event is intended for this provider.
        contextRequest.stopImmediatePropagation();
        if (contextRequest.wasHandled) {
            // Ignore requests from consumers that already have a resolved subscription.
            console.warn("Consumer was already handled.");
            return;
        }
        if (this.#isConsumerSubscribed(contextRequest.handleValueUpdate)) {
            contextRequest.subscription.reject(new Error("Consumer is already subscribed to the provider."));
            return;
        }
        // Provide the consumer with the current value immediately.
        contextRequest.handleValueUpdate(this.get());
        // Subscribe the consumer to future updates.
        const listeners = this.#station.on(ContextEventName.ValueUpdate, contextRequest.handleValueUpdate, contextRequest);
        const subscription = {
            unsubscribe() {
                listeners.off();
            },
        };
        contextRequest.subscription.resolve(subscription);
    }
    #onContextProviderAvailable() {
        const allListeners = this.#station.getListeners(ContextEventName.ValueUpdate);
        allListeners?.forEach((listener) => {
            const contextRequest = listener.context;
            if (contextRequest instanceof ContextRequestEvent === false) {
                // This should never happen.
                throw new Error("Invalid context request.");
            }
            contextRequest.resubscribe();
        });
    }
    hostConnected() {
        this.#emitter$
            .get()
            ?.dispatchEvent(new ContextProviderAvailableEvent(this._contextKey));
    }
}
